'use client';

import {
  FC,
  PropsWithChildren,
  useSyncExternalStore,
  type ReactNode
} from 'react';

/**
 * Empty subscribe function.
 * @returns An empty function.
 */
const emptySubscribe = () => () => {};
// eslint-disable-next-line -- for detecting the server.
const always = () => true;
// eslint-disable-next-line -- for detecting the client.
const never = () => false;

/**
 * Skip rendering `children` on the server.
 * Optionally render a `fallback` component instead.
 */
export const SkipRenderOnServer: FC<
  PropsWithChildren<{ fallback?: ReactNode }>
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- CSR doesn't support async
> = ({ children, fallback }) => {
  /**
   * Whether this component is rendering on the server.
   * @see {@link https://tkdodo.eu/blog/avoiding-hydration-mismatches-with-use-sync-external-store#usesyncexternalstore Avoiding Hydration Mismatches with useSyncExternalStore}
   * TL;DR: `useEffect` would require an additional render on the client.
   */
  const isServer = useSyncExternalStore(emptySubscribe, never, always);

  return isServer ? fallback : children;
};

/**
 * A higher order component that will only render the wrapped component on the client.
 * @param Component - The component to wrap.
 * @param fallback - The fallback component to render on the server.
 * @returns The wrapped component that will only render on the client.
 */
export function withSkipRenderOnServer<P extends object>(
  Component: FC<P>,
  fallback?: ReactNode
): FC<P> {
  return (props) => (
    <SkipRenderOnServer fallback={fallback}>
      <Component {...props} />
    </SkipRenderOnServer>
  );
}
